/**
 * Click-to-activate magnifier for product media images (desktop only).
 * Click zoom button to activate, move pointer over image to inspect,
 * leave container or click outside to deactivate.
 */

const hoverMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const bindings = new WeakMap();

function shouldEnableMagnifier() {
  return hoverMediaQuery.matches;
}

function resetContainerState(container) {
  container.classList.remove('is-magnifying');
  container.classList.remove('is-magnifier-active');

  const img = container.querySelector('.product-media__image');
  if (img) {
    img.style.transform = '';
    img.style.transformOrigin = '';
  }
}

function cleanupTarget(container) {
  const existing = bindings.get(container);

  if (existing) {
    container.removeEventListener('pointermove', existing.handleMove);
    container.removeEventListener('pointerenter', existing.handleEnter);
    container.removeEventListener('pointerleave', existing.handleLeave);
    container.removeEventListener('click', existing.handleContainerClick);

    existing.toggleButtons.forEach((button) => {
      button.removeEventListener('click', existing.handleToggleClick);
    });

    bindings.delete(container);
  }

  resetContainerState(container);
  container.removeAttribute('data-magnifier-bound');
}

function setupTarget(container) {
  if (!container || container.closest('zoom-dialog')) return;

  const img = container.querySelector('.product-media__image');
  if (!img) return;

  const scale = Number(container.dataset.magnifierScale || 2.5);
  let frame = null;
  let isActive = false;

  const toggleButtons = Array.from(
    container.querySelectorAll('[data-magnifier-toggle="true"]')
  );

  const updateOrigin = (event) => {
    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    img.style.transformOrigin = `${x}% ${y}%`;
  };

  const activateMagnifier = () => {
    if (!shouldEnableMagnifier()) return;
    if (container.offsetParent === null) return;

    isActive = true;
    container.classList.add('is-magnifier-active');
    container.classList.add('is-magnifying');
    img.style.transform = `scale(${scale})`;
  };

  const deactivateMagnifier = () => {
    isActive = false;
    resetContainerState(container);
  };

  const handleEnter = (event) => {
    if (!isActive) return;
    container.classList.add('is-magnifying');
    updateOrigin(event);
  };

  const handleMove = (event) => {
    if (!isActive) return;

    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => updateOrigin(event));
  };

  const handleLeave = () => {
    if (!isActive) return;
    deactivateMagnifier();
  };

  const handleContainerClick = (event) => {
    const toggle = event.target.closest('[data-magnifier-toggle="true"]');
    if (toggle) return;
    if (!isActive) return;

    updateOrigin(event);
  };

  const handleToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!shouldEnableMagnifier()) return;

    if (isActive) {
      deactivateMagnifier();
      return;
    }

    activateMagnifier();

    const rect = container.getBoundingClientRect();
    updateOrigin({
      clientX: rect.left + rect.width / 2,
      clientY: rect.top + rect.height / 2,
    });
  };

  container.addEventListener('pointerenter', handleEnter);
  container.addEventListener('pointermove', handleMove);
  container.addEventListener('pointerleave', handleLeave);
  container.addEventListener('click', handleContainerClick);

  toggleButtons.forEach((button) => {
    button.addEventListener('click', handleToggleClick);
  });

  container.setAttribute('data-magnifier-bound', 'true');
  bindings.set(container, {
    handleMove,
    handleEnter,
    handleLeave,
    handleContainerClick,
    handleToggleClick,
    toggleButtons,
  });
}

function initMagnifier() {
  const targets = document.querySelectorAll('[data-magnifier="true"]');

  targets.forEach((container) => cleanupTarget(container));

  if (!shouldEnableMagnifier()) return;

  targets.forEach((container) => setupTarget(container));
}

document.addEventListener('click', (event) => {
  const clickedInsideMagnifier = event.target.closest('[data-magnifier="true"]');
  if (clickedInsideMagnifier) return;

  document.querySelectorAll('[data-magnifier="true"].is-magnifier-active').forEach((container) => {
    resetContainerState(container);
  });
});

initMagnifier();
document.addEventListener('DOMContentLoaded', initMagnifier);
document.addEventListener('shopify:section:load', initMagnifier);
document.addEventListener('shopify:section:reorder', initMagnifier);
document.addEventListener('shopify:block:select', initMagnifier);

document.addEventListener('variant:update', () => {
  requestAnimationFrame(initMagnifier);
});

document.addEventListener('variant:selected', () => {
  requestAnimationFrame(initMagnifier);
});

if (hoverMediaQuery.addEventListener) {
  hoverMediaQuery.addEventListener('change', initMagnifier);
} else if (hoverMediaQuery.addListener) {
  hoverMediaQuery.addListener(initMagnifier);
}